import React from "react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";
import { Mountain, MapPin, Compass, Train, Zap, Users, Cloud, Building2, Map as MapIcon, ArrowRight } from "lucide-react";
import { PageHeader, KpiCard, SectionCard } from "@/components/Shared";
import { reserveDistribution, extractionTimeline, resourceBreakdown, tzaneenInfo } from "@/data/mineData";
import { miningSites } from "@/data/geoEsgData";
import { Link } from "react-router-dom";

const tooltipStyle = {
  contentStyle: { backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12 },
};

export default function Geological() {
  const total = reserveDistribution.reduce((s, r) => s + r.value, 0);
  return (
    <div data-testid="page-geological">
      <PageHeader eyebrow="Geological Overview" title="Reserves, Resources & Setting" subtitle="Total resource base, breakdown over remaining mine life — and what makes the Tzaneen location strategic." />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <KpiCard testid="kpi-reserves" label="Total Reserves" value={`${(total / 1_000_000).toFixed(1)}M`} unit="tons (P+P)" />
        <KpiCard testid="kpi-life" label="Reserve Life" value="18 yrs" unit="at current production" />
        <KpiCard testid="kpi-grade" label="Avg Ore Grade" value="3.2 g/t" unit="PGM equivalent" accent="amber" />
      </div>

      {/* Cross-link to Geological & ESG Intelligence (GIS map module) */}
      <Link
        to="/geo-esg"
        data-testid="geo-link-geo-esg"
        className="block mb-6 group bg-gradient-to-r from-slate-900 to-slate-800 rounded-lg p-5 text-white relative overflow-hidden hover:from-slate-800 hover:to-slate-700 transition-colors"
      >
        <div className="absolute right-4 top-4 opacity-30">
          <MapIcon size={48} className="text-amber-400" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-amber-400 font-semibold">
          Strategic GIS · Reserve Heat Map · ESG
        </div>
        <div className="font-display text-xl md:text-2xl font-semibold mt-1 max-w-xl">
          Explore {miningSites.length} tenements across SA — drill points, reserve grade zones, ESG and rehabilitation.
        </div>
        <div className="inline-flex items-center gap-1.5 mt-3 text-sm font-semibold text-amber-400 group-hover:gap-2 transition-all">
          Open Geological & ESG Intelligence
          <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
        </div>
      </Link>

      {/* Tzaneen area panel */}
      <SectionCard
        title="Tzaneen — Location & Setting"
        description="Why this location matters for the operation"
        action={<MapPin size={16} className="text-amber-700" />}
        className="mb-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="tzaneen-info-grid">
          <InfoRow icon={Compass} label="District" value={tzaneenInfo.district} />
          <InfoRow icon={MapPin} label="Province" value={tzaneenInfo.province} />
          <InfoRow icon={Mountain} label="Geological Setting" value={tzaneenInfo.geologicalSetting} />
          <InfoRow icon={Cloud} label="Climate" value={tzaneenInfo.climate} />
          <InfoRow icon={Building2} label="Distance from Polokwane" value={tzaneenInfo.distanceFromPolokwane} />
          <InfoRow icon={Users} label="Catchment Population" value={tzaneenInfo.populationCatchment} />
          <InfoRow icon={Train} label="Rail Connection" value={tzaneenInfo.rail} />
          <InfoRow icon={Zap} label="Power" value={tzaneenInfo.power} />
          <InfoRow icon={Users} label="Community" value={tzaneenInfo.community} />
        </div>

        <div className="mt-5 pt-5 border-t border-slate-100">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">Logistics — Nearest Ports</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tzaneenInfo.nearestPorts.map((p) => (
              <div key={p.name} className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-md p-3">
                <Train size={18} className="text-amber-700 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{p.name}</div>
                  <div className="text-xs text-slate-500">{p.distance}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-6">
        <SectionCard title="Reserve Distribution" description="Tons by commodity">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={reserveDistribution} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {reserveDistribution.map((d) => <Cell key={d.name} fill={d.color} />)}
                </Pie>
                <Tooltip {...tooltipStyle} formatter={(v) => `${(v / 1_000_000).toFixed(2)}M tons`} />
                <Legend verticalAlign="bottom" iconType="square" wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Extraction Timeline" description="Projected reserves remaining" className="xl:col-span-2">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={extractionTimeline} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <defs>
                  <linearGradient id="resGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#B45309" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#B45309" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="year" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1_000_000}M`} />
                <Tooltip {...tooltipStyle} formatter={(v) => `${(v / 1_000_000).toFixed(1)}M tons`} />
                <Area type="monotone" dataKey="reserves" stroke="#B45309" fill="url(#resGrad)" strokeWidth={2.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <SectionCard title="Proven vs Probable" description="Resource classification by commodity (tons)">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={resourceBreakdown} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="category" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1_000_000}M`} />
                <Tooltip {...tooltipStyle} formatter={(v) => `${(v / 1_000_000).toFixed(2)}M tons`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="coal" stackId="a" fill="#0F172A" name="Coal" />
                <Bar dataKey="pgm" stackId="a" fill="#B45309" name="PGM" />
                <Bar dataKey="chrome" stackId="a" fill="#475569" name="Chrome" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        <SectionCard title="Stratigraphic Section" description="Tzaneen orebody — schematic">
          <div className="relative h-72 rounded-md overflow-hidden bg-gradient-to-b from-amber-50 via-stone-100 to-slate-200 border border-slate-200">
            <div className="absolute inset-0 grain" />
            <div className="absolute top-4 left-4 right-4 flex items-center gap-2 text-[11px] uppercase tracking-wider text-slate-700 font-semibold">
              <Mountain size={14} /> Bushveld eastern limb
            </div>
            <div className="absolute inset-x-0 top-12 bottom-0 flex flex-col">
              {[
                { label: "Overburden / Topsoil", color: "bg-stone-300", h: "h-10" },
                { label: "Coal Seam A (1.8m)", color: "bg-slate-900", h: "h-12" },
                { label: "Mudstone Interburden", color: "bg-stone-400", h: "h-8" },
                { label: "Coal Seam B (2.4m)", color: "bg-slate-800", h: "h-14" },
                { label: "Bushveld PGM Reef", color: "bg-amber-700", h: "h-10" },
                { label: "Chromitite Layer", color: "bg-slate-600", h: "h-10" },
                { label: "Bedrock", color: "bg-stone-500", h: "h-12" },
              ].map((l) => (
                <div key={l.label} className={`${l.color} ${l.h} flex items-center px-4 text-[11px] text-white/95 font-medium tracking-tight border-t border-white/20`}>
                  {l.label}
                </div>
              ))}
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 p-3 bg-slate-50/60 border border-slate-100 rounded-md">
      <div className="h-8 w-8 rounded-md bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
        <Icon size={14} className="text-amber-700" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="text-sm text-slate-800 mt-0.5">{value}</div>
      </div>
    </div>
  );
}
